/*
 * Request pieces both V2 trees send: the mentor-list request (E2) and the
 * successor element of a create (E4) or an edit (E6).
 *
 * Both writes keep V1's bare `details[]` array; element 0 differs between add and
 * edit, so each tree builds its own, and the successor element is built here.
 */

import { ASSIGN_MODES } from "../MentorAssignPanel/constants";
import { toTimingsPayload } from "./timings";

/**
 * The E3 preview body (`POST /batch-flow/preview`), which is also what travels as
 * E2's `predecessor` for a successor's check — one shape, built once.
 *
 * `figures` are E1's or E5's own numbers, sent back untouched: the backend
 * recomputes the ceiling around a previous batch, so nothing here adds up to it
 * from the rest of the response.
 *
 * @param {Object} input
 * @param {"credit"|"flex"} input.kind
 * @param {string} input.startDate - "YYYY-MM-DD"
 * @param {{day: string, time: string}[]} input.days
 * @param {number|string} input.duration - minutes
 * @param {number|string|null} [input.sessionCount] - credit only; ignored for flex
 * @param {Object} [input.figures] - sessionCeilingParts, validityEnd, durationMonths,
 *   weeklyFrequency, flexAnchor — whichever the response carried
 * @param {Object|null} [input.batch] - E5's block; its presence is what makes it an edit
 * @returns {object}
 */
export const buildPreviewRequest = ({
  kind,
  startDate,
  days,
  duration,
  sessionCount = null,
  figures = {},
  batch = null,
}) => ({
  kind,
  schedule: {
    startDate,
    timings: toTimingsPayload(days, duration),
    ...(sessionCount === null || sessionCount === "" ? {} : { sessionCount: Number(sessionCount) }),
  },
  figures,
  batch,
});

/**
 * The E2 request (`POST /batch-flow/mentors`).
 * @param {Object} input
 * @param {string} input.studentId
 * @param {string} input.orderId
 * @param {string} input.courseId
 * @param {string} [input.startDate] - "YYYY-MM-DD"; the batch's own stored start on
 *   edit, past or not — the backend judges a started batch from its first class day
 *   ahead itself (R-05-05). With a `predecessor`, only a start the ops user moved
 *   later than the earliest one (Q-D3); null lets the backend derive it
 * @param {{day: string, time: string}[]} input.days
 * @param {number|string} input.duration - minutes
 * @param {number|string|null} [input.sessionCount] - omitted for flex, which the
 *   backend counts from the validity window (R-03-04). A count sent for a flex
 *   course is used AS SENT, so a stale one checks the wrong window
 * @param {string|null} [input.batchId] - the batch and its chain are excluded from
 *   the holds; on edit it is also what marks the start as unchanged
 * @param {"list"|"search"|"check"} input.mode
 * @param {string} [input.term] - search mode
 * @param {number} [input.mentorId] - check mode
 * @param {Object|null} [input.predecessor] - a successor's check: the HEAD's preview
 *   body. The backend derives the successor's window from it and answers `window`
 * @returns {object}
 */
export const buildMentorListRequest = ({
  studentId,
  orderId,
  courseId,
  startDate = null,
  days,
  duration,
  sessionCount = null,
  batchId = null,
  mode,
  term = null,
  mentorId = null,
  predecessor = null,
}) => ({
  studentId,
  orderId,
  courseId,
  schedule: {
    /* A successor begins where the head ends unless the ops user moved it later,
       and the backend derives that from `predecessor`. */
    startDate,
    timings: toTimingsPayload(days, duration),
    ...(sessionCount === null || sessionCount === "" ? {} : { sessionCount: Number(sessionCount) }),
  },
  ...(predecessor ? { predecessor } : {}),
  batchId,
  mode,
  term,
  mentorId,
});

/** The E2 modes, by name. */
export const MENTOR_LIST_MODES = { LIST: "list", SEARCH: "search", CHECK: "check" };

/**
 * A NEW successor (`isExistingBatch: false`). Its start is the backend's earliest
 * start after the head (R-07-05) unless the ops user moved it later, and only then
 * does `startingDate` travel (Q-D3). On an edit, leaving out `days`/`mentorId`
 * means "same as the head's" (E6); on a create they are required (E4), and the
 * caller passes the head's when "same as above" is ticked.
 * @param {Object} input
 * @param {string} input.batchName - the typed name, which is its id at creation
 * @param {string} input.courseId - the successor's own course
 * @param {string|null} [input.orderId] - the purchase that course is on; without it
 *   the backend takes the first order carrying the course, wrong for one bought twice
 * @param {number|string} input.session - the successor's own count (R-07-03)
 * @param {{day: string, time: string}[]} [input.days]
 * @param {number|string} [input.duration]
 * @param {number|null} [input.mentorId]
 * @param {string} [input.startingDate] - "YYYY-MM-DD", only when moved later
 * @returns {object}
 */
export const buildNewSuccessorEntry = ({
  batchName,
  courseId,
  orderId,
  session,
  days,
  duration,
  mentorId,
  startingDate,
}) => ({
  batchId: batchName?.trim?.() ?? "",
  courseId,
  ...(orderId ? { orderId } : {}),
  isStitched: true,
  isExistingBatch: false,
  session: Number(session),
  ...(days ? { timings: toTimingsPayload(days, duration) } : {}),
  ...(mentorId !== null && mentorId !== undefined ? { mentorId } : {}),
  ...(startingDate ? { startingDate } : {}),
});

/**
 * An existing batch linked as the successor (R-07-06): its dates move to its
 * earliest start after the head, or to a later `startingDate` the ops user typed;
 * its mentor and times stay.
 * @param {string} batchId - one of the `stitchTargets`
 * @param {string} [startingDate] - "YYYY-MM-DD", only when moved later
 * @returns {object}
 */
export const buildLinkTargetEntry = (batchId, startingDate) => ({
  batchId,
  isStitched: true,
  isExistingBatch: true,
  ...(startingDate ? { startingDate } : {}),
});

/**
 * The mentor id a mentor section resolves to, or null. Only a chosen mentor who
 * is not occupied counts (R-05-10); choosing is the assignment, there is no
 * separate assign control.
 * @param {{mode: string, assigned: boolean, known?: object, bestPick?: object,
 *          searchPick?: object}} selection
 * @returns {number|null}
 */
export const assignedMentorId = (selection) => {
  if (!selection?.assigned) return null;
  const mentor = {
    [ASSIGN_MODES.KNOWN]: selection?.known,
    [ASSIGN_MODES.BEST]: selection?.bestPick,
    [ASSIGN_MODES.SEARCH]: selection?.searchPick,
  }?.[selection?.mode];
  if (!mentor || mentor?.status === "occupied") return null;
  return mentor?.id ?? null;
};
